/**
 * Leaderboard / Leagues (Duolingo style). Shows the server-synchronized
 * ranking for a league (global or academy), grouped by TIER
 * (diamond/gold/silver/bronze) with promotion/demotion zones.
 *
 * Privacy (opt-in): ONLY people who consented appear — the server filters
 * `opted_in` in the stream, so the client never sees anyone who did not opt in. A toggle
 * lets the user appear/disappear. The view derives the ranking and tiers with the
 * `gamification/leagues.ts` core (`rankLeaderboard` + `assignTier`).
 */
import { type Component, createMemo, createSignal, For, type JSX, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Crown, Gem, Medal, ShieldCheck, Trophy } from "@fleet/icons";
import {
  assignTier,
  type LeaderboardEntry,
  type LeagueTier,
  rankLeaderboard,
  updateLeaderboardOptInMutation,
} from "@aula/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Skeleton, Switch } from "@fleet/ui";
import { Mascot } from "@/components/ui/mascot";
import {
  useCourseWrite,
  useLeaderboard,
  useLeaderboardOptIn,
  useMyOrganizations,
} from "../../hooks/use-course-data";

const GLOBAL_LEAGUE = "global";

const TIER_ORDER: readonly LeagueTier[] = ["diamond", "gold", "silver", "bronze"];

const TIER_META: Record<LeagueTier, { label: () => string; icon: () => JSX.Element; cls: string }> =
  {
    diamond: {
      label: () => "Diamond",
      icon: () => <Gem class="h-4 w-4" />,
      cls: "bg-sky-500/15 text-sky-500 ring-sky-500/35",
    },
    gold: {
      label: () => "Gold",
      icon: () => <Crown class="h-4 w-4" />,
      cls: "bg-joy-sun/20 text-joy-sun-foreground ring-joy-sun/40",
    },
    silver: {
      label: () => "Silver",
      icon: () => <ShieldCheck class="h-4 w-4" />,
      cls: "bg-muted text-muted-foreground ring-border",
    },
    bronze: {
      label: () => "Bronze",
      icon: () => <Medal class="h-4 w-4" />,
      cls: "bg-joy-flame/12 text-joy-flame ring-joy-flame/30",
    },
  };

interface RankedRow extends LeaderboardEntry {
  rank: number;
  tier: LeagueTier;
}

export const LeaderboardView: Component = () => {
  const { t } = useI18n();
  const { drizzle, userId } = useCourseWrite();
  const [league, setLeague] = createSignal<string>(GLOBAL_LEAGUE);
  const orgs = useMyOrganizations();
  const board = useLeaderboard(league);
  const optIn = useLeaderboardOptIn();

  const optedIn = () => (optIn.query.data?.leaderboard_opt_in ?? 0) === 1;

  // Ranking + tiers derived with the core (all synced rows already
  // have consent → opdedIn: true).
  const ranked = createMemo<RankedRow[]>(() => {
    const rows = board.data ?? [];
    const entries: LeaderboardEntry[] = rows.map((r) => ({
      playerId: r.user_id ?? "",
      xp: r.total_xp ?? 0,
      opdedIn: true,
      displayName: r.display_name ?? undefined,
    }));
    const list = rankLeaderboard(entries);
    const total = list.length;
    return list.map((e) => ({ ...e, tier: assignTier(e.rank, total) }));
  });

  const byTier = (tier: LeagueTier) => ranked().filter((r) => r.tier === tier);

  const toggleOptIn = async (next: boolean) => {
    const db = drizzle();
    const uid = userId();
    if (!db || !uid) {
      return;
    }
    await updateLeaderboardOptInMutation(db, uid, next);
  };

  const leagues = createMemo(() => [
    { id: GLOBAL_LEAGUE, name: t(msg`Global`) },
    ...(orgs.data ?? []).map((o) => ({ id: o.id, name: o.name ?? t(msg`Academy`) })),
  ]);

  return (
    <div class="mx-auto max-w-3xl space-y-6 p-4 md:p-6" data-testid="leaderboard-view">
      {/* Hero */}
      <div class="aula-mesh relative overflow-hidden rounded-3xl border border-border p-5 md:p-6">
        <div class="flex items-center gap-4">
          <Mascot mood="celebrate" class="h-16 w-16 shrink-0 animate-bob md:h-20 md:w-20" />
          <div class="min-w-0">
            <h1 class="font-display text-2xl font-extrabold tracking-tight md:text-3xl">
              {t(msg`Leagues`)}
            </h1>
            <p class="text-sm text-muted-foreground">
              {t(msg`Climb the ranks by earning XP. Top learners get promoted.`)}
            </p>
          </div>
          <Trophy class="ml-auto hidden h-10 w-10 text-accent sm:block" />
        </div>
      </div>

      {/* Consentimiento (opt-in de privacidad) */}
      <div
        class="flex items-center gap-4 rounded-2xl border border-border bg-surface p-4"
        data-testid="leaderboard-optin"
      >
        <div class="min-w-0 flex-1">
          <p class="font-display font-bold">{t(msg`Appear on the leaderboard`)}</p>
          <p class="text-sm text-muted-foreground">
            {t(msg`You choose whether to be listed. When off, no one sees you here.`)}
          </p>
        </div>
        <Switch checked={optedIn()} onCheckedChange={(v) => void toggleOptIn(v)} />
      </div>

      {/* Selector de liga (global + academias) */}
      <Show when={leagues().length > 1}>
        <div class="flex flex-wrap gap-2" data-testid="leaderboard-league-tabs">
          <For each={leagues()}>
            {(l) => (
              <button
                type="button"
                onClick={() => setLeague(l.id)}
                data-testid={`league-tab-${l.id}`}
                aria-pressed={league() === l.id}
                class={`rounded-2xl px-4 py-2 text-sm font-semibold ring-1 ring-inset transition-colors ${
                  league() === l.id
                    ? "bg-accent text-accent-foreground ring-accent/40"
                    : "bg-surface text-muted-foreground ring-border hover:text-foreground"
                }`}
              >
                {l.name}
              </button>
            )}
          </For>
        </div>
      </Show>

      {/* Ranking por tiers */}
      <Show
        when={!board.isLoading}
        fallback={
          <div class="space-y-3">
            <For each={Array.from({ length: 5 })}>
              {() => <Skeleton class="h-14 w-full rounded-2xl" />}
            </For>
          </div>
        }
      >
        <Show
          when={ranked().length > 0}
          fallback={
            <div
              class="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border p-14 text-center"
              data-testid="leaderboard-empty"
            >
              <Mascot mood="think" class="mb-4 h-24 w-24" />
              <h3 class="font-display text-lg font-bold">{t(msg`No one is competing yet`)}</h3>
              <p class="mt-1 max-w-xs text-sm text-muted-foreground">
                {t(msg`Earn XP and turn on the toggle above to join the league.`)}
              </p>
            </div>
          }
        >
          <div class="space-y-5">
            <For each={TIER_ORDER}>
              {(tier) => (
                <Show when={byTier(tier).length > 0}>
                  <section data-testid={`tier-section-${tier}`}>
                    <div
                      class={`mb-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-bold ring-1 ring-inset ${TIER_META[tier].cls}`}
                    >
                      {TIER_META[tier].icon()}
                      <span data-testid={`tier-label-${tier}`}>{TIER_META[tier].label()}</span>
                    </div>
                    <ul class="space-y-2">
                      <For each={byTier(tier)}>
                        {(row) => (
                          <li
                            data-testid="leaderboard-row"
                            data-user={row.playerId}
                            class={`flex items-center gap-3 rounded-2xl border p-3 ${
                              row.playerId === userId()
                                ? "border-accent/50 bg-accent/10"
                                : "border-border bg-surface"
                            }`}
                          >
                            <span class="w-6 text-center font-display text-lg font-extrabold text-muted-foreground">
                              {row.rank}
                            </span>
                            <span class="min-w-0 flex-1 truncate font-semibold">
                              {row.displayName || t(msg`Learner`)}
                              <Show when={row.playerId === userId()}>
                                <span class="ml-2 rounded-full bg-accent px-2 py-0.5 text-xs font-bold text-accent-foreground">
                                  {t(msg`You`)}
                                </span>
                              </Show>
                            </span>
                            <span class="font-display font-extrabold text-accent">{row.xp} XP</span>
                          </li>
                        )}
                      </For>
                    </ul>
                  </section>
                </Show>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
};
